import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Notifications as NotificationsIcon } from '@mui/icons-material';
import Badge from '@mui/material/Badge';
import Popover from '@mui/material/Popover';
import { useNavigate } from 'react-router-dom';

import * as S from './styles';
import { notificationsServiceNewStudent } from '../../../services/newStudent/notifications/notifications.service';
import { IStudentNotification } from '../../../interfaces/newStudent/notification.interface';

function formatNotificationDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours === 0) {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      if (diffMins < 1) return 'Agora';
      return `Há ${diffMins} min`;
    }
    return `Há ${diffHours}h`;
  }
  if (diffDays === 1) return 'Ontem';
  if (diffDays < 7) return `Há ${diffDays} dias`;

  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function resolveNotificationPath(link: string | null): string | null {
  if (!link) return null;

  if (link.startsWith('/new/aluno')) {
    return link;
  }

  if (link.startsWith('/')) {
    return `/new/aluno${link}`;
  }

  return `/new/aluno/${link}`;
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const bellRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [list, setList] = useState<IStudentNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const open = Boolean(anchorEl);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const { result, data } = await notificationsServiceNewStudent.unreadCount();
      if (result && data) {
        setUnreadCount(data.count);
      }
    } catch {
      setUnreadCount(0);
    }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { result, data } = await notificationsServiceNewStudent.show();
      if (result && data) {
        setList(data);
      } else {
        setList([]);
      }
    } catch {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = window.setInterval(fetchUnreadCount, 60000);
    return () => window.clearInterval(interval);
  }, [fetchUnreadCount]);

  const handleClick = () => {
    setAnchorEl(bellRef.current);
    fetchList();
  };

  const handleClose = () => {
    setAnchorEl(null);
    fetchUnreadCount();
  };

  const handleMarkAllAsRead = async () => {
    try {
      const { result } = await notificationsServiceNewStudent.markAllAsRead();
      if (result) {
        setList((prev) => prev.map((item) => ({ ...item, read: true })));
        setUnreadCount(0);
      }
    } catch {
      // silenciar
    }
  };

  const handleNotificationClick = async (item: IStudentNotification) => {
    if (!item.read) {
      try {
        await notificationsServiceNewStudent.markAsRead(item.id);
        setList((prev) =>
          prev.map((notification) =>
            notification.id === item.id
              ? { ...notification, read: true }
              : notification,
          ),
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch {
        // silenciar
      }
    }

    handleClose();

    const target = resolveNotificationPath(item.link);
    if (target) {
      navigate(target);
    }
  };

  return (
    <>
      <S.BellWrapper ref={bellRef}>
        <S.BellButton type="button" onClick={handleClick} aria-label="Notificações">
          <Badge
            badgeContent={unreadCount > 0 ? unreadCount : 0}
            color="error"
            max={99}
          >
            <NotificationsIcon fontSize="large" />
          </Badge>
        </S.BellButton>
      </S.BellWrapper>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1.5,
              overflow: 'visible',
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            },
          },
        }}
      >
        <S.PopoverPaper>
          <S.PopoverHeader>
            <S.PopoverTitle>Notificações</S.PopoverTitle>
            {unreadCount > 0 && (
              <S.MarkAllButton type="button" onClick={handleMarkAllAsRead}>
                Marcar todas como lidas
              </S.MarkAllButton>
            )}
          </S.PopoverHeader>
          <S.List>
            {loading ? (
              <S.EmptyMessage>Carregando...</S.EmptyMessage>
            ) : list.length === 0 ? (
              <S.EmptyMessage>Nenhuma notificação.</S.EmptyMessage>
            ) : (
              list.map((item) => (
                <S.Item
                  key={item.id}
                  $unread={!item.read}
                  onClick={() => handleNotificationClick(item)}
                >
                  <S.ItemTitle>{item.title}</S.ItemTitle>
                  <S.ItemMessage>{item.message}</S.ItemMessage>
                  <S.ItemDate>
                    {formatNotificationDate(item.createdAt)}
                  </S.ItemDate>
                </S.Item>
              ))
            )}
          </S.List>
        </S.PopoverPaper>
      </Popover>
    </>
  );
}
